import { DEFAULT_TIME } from "@/components/BusinessHours/types";
import { TimeFormat, TimeOfDayValue } from "@/types/time";

export const formatNumberTimeToString = (time: number) => {
  // `0` is a valid value and must return "00".
  if (time === undefined || time === null) return;
  if (Number.isNaN(time)) return;

  let stringifiedNumber = time.toString();
  if (stringifiedNumber?.length === 1) {
    stringifiedNumber = "0" + stringifiedNumber;
  }

  return stringifiedNumber;
};

export const formatTimeLength = (time: number) => {
  let formattedTime = time.toString();

  if (formattedTime.length === 1) {
    formattedTime = `0${formattedTime}`;
  }

  return formattedTime;
};

export const formatToBusinessHoursInput = (time: string, format: TimeFormat) => {
  if (time === undefined || time === "") return;

  let formattedTime = time;

  if (format === "AM/PM") {
    const [hourString, minutesString] = time.split(":");
    const hourNumber = Number(hourString);
    const minutesNumber = Number(minutesString);

    let period = "am";
    let hour12Format = hourNumber;

    if (hourNumber === 0) {
      // 00:00 to 12:00 AM
      hour12Format = 12;
      period = "am";
    } else if (hourNumber === 12) {
      // 12:00 to 12:00 PM
      hour12Format = 12;
      period = "pm";
    } else if (hourNumber > 12) {
      hour12Format = hourNumber - 12;
      period = "pm";
    } else {
      period = "am";
    }

    formattedTime = `${formatTimeLength(hour12Format)}:${formatTimeLength(minutesNumber)}${period}`;
  }

  return formattedTime;
};

export const formatToBusinessHoursPreview = (time: string, format: TimeFormat) => {
  if (time === undefined) return;

  if (time === "") {
    const hourNumber = Number(DEFAULT_TIME.split(":")[0]);

    return `${DEFAULT_TIME}${format === "AM/PM" ? (hourNumber >= 12 ? "pm" : "am") : ""}`;
  }

  let formattedTime = time;

  if (format === "AM/PM") {
    const hourNumber = Number(time.split(":")[0]);
    const minutesNumber = Number(time.split(":")[1]);

    if (Number.isNaN(hourNumber) || Number.isNaN(minutesNumber)) return;

    if (hourNumber === 0) {
      formattedTime = `12:${formatTimeLength(minutesNumber)}am`;
    } else if (hourNumber === 12) {
      formattedTime = `12:${formatTimeLength(minutesNumber)}pm`;
    } else if (hourNumber > 12) {
      formattedTime = `${formatTimeLength(hourNumber - 12)}:${formatTimeLength(minutesNumber)}pm`;
    } else {
      formattedTime = `${formatTimeLength(hourNumber)}:${formatTimeLength(minutesNumber)}am`;
    }
  }

  return formattedTime;
};

const padToTwoDigits = (value: number) => String(value).padStart(2, "0");

const isValidTimeOfDayValue = (value: TimeOfDayValue): boolean => {
  return (
    Number.isInteger(value.hours) &&
    Number.isInteger(value.minutes) &&
    value.hours >= 0 &&
    value.hours <= 23 &&
    value.minutes >= 0 &&
    value.minutes <= 59
  );
};

export const parseTimeStringToTimeOfDayValue = (time?: string): TimeOfDayValue | undefined => {
  if (!time) return undefined;

  const [hoursRaw, minutesRaw] = time.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return undefined;

  const value: TimeOfDayValue = { hours, minutes };
  return isValidTimeOfDayValue(value) ? value : undefined;
};

export const formatTimeOfDayValueToTimeString = (value?: TimeOfDayValue): string | undefined => {
  if (!value) return undefined;
  if (!isValidTimeOfDayValue(value)) return undefined;

  return `${padToTwoDigits(value.hours)}:${padToTwoDigits(value.minutes)}`;
};
